/**
 * Карта в ответе: кадр контура там, где ответ назвал контуры поимённо.
 *
 * Блок `map` контракта несёт СЛОЙ и ВЫБОРКУ, а не геометрию — и это правильно:
 * иначе сервер начал бы диктовать рисование, а клиент хранил бы второй
 * источник тех же чисел. Из этого раньше следовал отказ рисовать что-либо
 * вовсе. Следствие было сильнее причины: выборка вида `contour:1204` НАЗЫВАЕТ
 * единицы, и показать их превью — не выдумка, а тот же жест, что на карте и в
 * обзоре района, только начатый с другой стороны.
 *
 * Поэтому здесь ровно две ветки, и обе честные:
 *
 * * **выборка называет контуры** — превью каждого из них `MapPreviewCard`,
 *   тем же компонентом и с теми же величинами, что на карте. Свойства фичи
 *   берутся ИЗ КАРТЫ (`/map/reference` → рамка района → `/map/contours`), а не
 *   собираются из карточки контура: у карточки нет ни яруса связи, ни
 *   отношения геометрии, и подставить туда правдоподобное значило бы
 *   придумать величину, которой никто не измерял;
 * * **выборка называет что-то другое** (массив, слой, область) — блок
 *   по-прежнему называет слой и выборку словами и ведёт на карту. Врать
 *   полигоном «примерно отсюда» нельзя.
 *
 * Запрос идёт ТОЛЬКО после раскрытия: ответ в кармане уровня не имеет права
 * стоить экрану двух тяжёлых запросов карты за то, что человек его просто
 * прочитал.
 */

import { useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import type { ContourFeatureProps } from "../../api/client";
import { useAnalysisContext } from "../../analysis/context";
import { DataStatePanel } from "../../components/ui";
import { DENSITY } from "../../lib/levels";
import { MapPreviewCard } from "../map/MapPreviewCard";
import { DISTRICT_FALLBACK_BBOX, geoBounds } from "../map/mapModel";
import styles from "./ai.module.css";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";

const m = defineMessages({
  loading: {
    id: "answer.map.loading",
    defaultMessage: "Загружаем кадры контуров",
    description: "Заголовок индикатора загрузки под блоком «карта» в ответе ИИ: подгружаются кадры контуров",
  },
  error: {
    id: "answer.map.error",
    defaultMessage: "Кадры контуров не загрузились",
    description: "Заголовок ошибки под блоком «карта» в ответе ИИ; ниже — текст ошибки сервера",
  },
  rest: {
    id: "answer.map.rest",
    defaultMessage: "{count, plural, one {Ещё # контур ответа не показан кадром} few {Ещё # контура ответа не показаны кадром} many {Ещё # контуров ответа не показаны кадром} other {Ещё # контура ответа не показаны кадром}}: список целиком — на экране карты.",
    description: "Примечание под кадрами контуров в ответе ИИ; {count} — сколько контуров из ответа не поместилось в превью (число)",
  },
  missingTitle: {
    id: "answer.map.missing.title",
    defaultMessage: "Кадром не показаны",
    description: "Заголовок панели «не показаны» под блоком «карта» в ответе ИИ",
  },
  missingText: {
    id: "answer.map.missing.text",
    defaultMessage: "{count, plural, one {Контур {list} назван ответом, но не нашёлся} few {Контуры {list} названы ответом, но не нашлись} many {Контуры {list} названы ответом, но не нашлись} other {Контуры {list} названы ответом, но не нашлись}} в обзорном кадре карты — показать здесь нечем.",
    description: "Текст панели «не показаны»; {count} — число контуров, {list} — их номера через запятую",
  },
  truncated: {
    id: "answer.map.truncated",
    defaultMessage: "Обзорная выдача обрезана лимитом: часть контуров кадра просто не доехала.",
    description: "Добавка к тексту «не показаны», когда выдача карты обрезана лимитом",
  },
  emptyTitle: {
    id: "answer.map.empty.title",
    defaultMessage: "Кадров нет",
    description: "Заголовок пустой панели под блоком «карта» в ответе ИИ",
  },
  emptyText: {
    id: "answer.map.empty.text",
    defaultMessage: "Выборка ответа не совпала ни с одним контуром обзорного кадра.",
    description: "Текст пустой панели под блоком «карта» в ответе ИИ",
  },
});

/** Зум обзорного кадра: тот же, что у обзора района. */
const OVERVIEW_ZOOM = 11;

/**
 * Контуры, названные выборкой блока.
 *
 * Форма выборки закрыта сервером, и разбор здесь НАМЕРЕННО узкий: только
 * `contour:<id>` и `contours:<id>,<id>`. Выборка вида `massif:12` контуров не
 * называет — она называет массив, и вытащить из неё список контуров можно
 * только вторым запросом, то есть догадкой о том, что сервер имел в виду.
 * Догадка тут дороже отказа: показать не те полигоны хуже, чем не показать
 * никаких.
 */
export function selectionContours(selection: string): number[] {
  const match = /^contours?:([0-9,\s]+)$/.exec(selection.trim());
  if (!match) return [];
  const ids = (match[1] ?? "")
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((value) => Number.isInteger(value) && value > 0);
  return [...new Set(ids)];
}

type Loaded =
  | { state: "loading" }
  | { state: "error"; message: string }
  | {
      state: "ready";
      found: ContourFeatureProps[];
      missing: number[];
      names: Map<number, string>;
      /** Обзорная выдача обрезана лимитом: часть контуров просто не доехала. */
      truncated: boolean;
    };

/**
 * Кадры контуров, названных ответом.
 *
 * Компонент монтируется только раскрытием, поэтому запрос и живёт в нём: до
 * раскрытия его нет вовсе, а не «есть, но отменён».
 */
export function AnswerContourPreview({ ids }: { ids: number[] }) {
  const t = useT();
  const context = useAnalysisContext();
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });
  const key = ids.join(",");

  useEffect(() => {
    let cancelled = false;
    setLoaded({ state: "loading" });
    const wanted = new Set(ids);

    // Рамка района — из справочного слоя, как на обзорном кадре: `/map/contours`
    // без рамки не отвечает, а придумывать её из головы значит просить не тот
    // кусок земли.
    const run = async () => {
      const reference = await api.mapReference({
        ...context.params,
        zoom: OVERVIEW_ZOOM,
      });
      const bbox =
        geoBounds(reference.district.features ?? []) ?? DISTRICT_FALLBACK_BBOX;
      const [tiles, massifs] = await Promise.all([
        api.mapContours({
          ...context.params,
          bbox: bbox.join(","),
          zoom: OVERVIEW_ZOOM,
        }),
        api.massifs(context.params),
      ]);
      const found: ContourFeatureProps[] = [];
      for (const feature of tiles.features ?? []) {
        if (wanted.has(feature.properties.contour_id)) found.push(feature.properties);
      }
      const names = new Map<number, string>();
      for (const item of massifs.items ?? []) names.set(item.id, item.canonical_name);
      const seen = new Set(found.map((props) => props.contour_id));
      return {
        state: "ready" as const,
        found,
        missing: ids.filter((id) => !seen.has(id)),
        names,
        truncated: tiles.truncated,
      };
    };

    run()
      .then((result) => {
        if (!cancelled) setLoaded(result);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setLoaded({
          state: "error",
          message: cause instanceof ApiError ? cause.message : String(cause),
        });
      });
    return () => {
      cancelled = true;
    };
    // `key` вместо массива: ссылка на массив меняется каждый рендер, и без
    // строки запрос уходил бы бесконечно.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.key, key]);

  if (loaded.state === "loading") {
    return <DataStatePanel state="loading" title={t(m.loading)} />;
  }
  if (loaded.state === "error") {
    return (
      <DataStatePanel state="error" title={t(m.error)}>
        {loaded.message}
      </DataStatePanel>
    );
  }

  // Бюджет плотности — тот же, что у любого списка внутри блока: превью
  // контура занимает целый экран телефона, и пять подряд это уже реестр, а не
  // ответ.
  const shown = loaded.found.slice(0, DENSITY.preview);
  const rest = loaded.found.length - shown.length;

  return (
    <div className={styles.answer}>
      {shown.map((props) => (
        <MapPreviewCard
          key={props.contour_id}
          props={props}
          massifName={loaded.names.get(props.massif_id) ?? null}
        />
      ))}
      {rest > 0 ? (
        <p className={styles.note}>
          {t(m.rest, { count: rest })}
        </p>
      ) : null}
      {loaded.missing.length > 0 ? (
        // Клиент знает РОВНО одно: названного контура нет среди фич обзорного
        // кадра. Почему — он не знает: выдача режется лимитом (`truncated`),
        // областью чтения и рамкой кадра. Сказать «принятой геометрии нет»
        // значило бы напечатать «не нашёл» как утверждение о данных — ровно то,
        // что канон запрещает в первую очередь.
        <DataStatePanel state="missing" title={t(m.missingTitle)}>
          {t(m.missingText, {
            count: loaded.missing.length,
            list: loaded.missing.join(", "),
          })}
          {loaded.truncated ? ` ${t(m.truncated)}` : ""}
        </DataStatePanel>
      ) : null}
      {loaded.found.length === 0 && loaded.missing.length === 0 ? (
        <DataStatePanel state="empty" title={t(m.emptyTitle)}>
          {t(m.emptyText)}
        </DataStatePanel>
      ) : null}
    </div>
  );
}
